import React, { useState, useRef } from 'react';

export default function SuggestionsPage() {
  const form = useRef();
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendEmail = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    const formData = {
      name: form.current.user_name.value || 'Anonymous',
      email: form.current.user_email.value || 'No email provided',
      subject: `New Prompt Suggestion: ${form.current.prompt_type.value}`,
      message: `Prompt Type: ${form.current.prompt_type.value}\n\nDescription: ${form.current.suggestion.value}`
    };

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you for your suggestion! We\'ll review it and consider adding it to our platform.'
        });
        form.current.reset();
      } else {
        throw new Error(data.details || data.error || 'Failed to send suggestion');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        type: 'error',
        message: `Error: ${error.message}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Suggest a New Prompt</h1>
        
        <div className="prose prose-lg text-gray-600 mb-8">
          We're always looking to improve and expand our prompt collection. If you have an idea for a new prompt type that you'd like to see on our platform, please let us know!
        </div>

        <form ref={form} onSubmit={sendEmail} className="space-y-6">
          {/* Name Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Your Name (Optional)
            </label>
            <input
              type="text"
              name="user_name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Your Email (Optional)
            </label>
            <input
              type="email"
              name="user_email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>

          {/* Prompt Type Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Type of Prompt You'd Like to See <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="prompt_type"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., YouTube Description Generator"
            />
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Description of Your Suggestion <span className="text-red-500">*</span>
            </label>
            <textarea
              name="suggestion"
              required
              rows={7}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder="Please describe what kind of prompt you'd like to see and how it would be helpful..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-2 text-white bg-primary rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors duration-200 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Suggestion'}
            </button>
          </div>

          {/* Status Message */}
          {status.message && (
            <div
              className={`p-4 rounded-md ${
                status.type === 'success'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}
            >
              {status.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
